// ---------------------------------------------------------------------------
// UN SEUL MENU « VOIX » — ET LES LIGNES DU PANNEAU « CHANGEMENT DE SOURCE ».
//
// Toji (10/09) : « on a un doublon au niveau de la gestion de la voix : le
// switch entre deux fichiers et le switch avec la voix directement. Je veux que
// le switch de fichiers soit dans l'onglet de la gestion de la voix. Quand il
// n'y a qu'une seule version, le bouton n'apparaît pas — un vieil animé
// uniquement en japonais, il n'y a qu'une version, pas besoin d'afficher deux
// boutons. Quand il n'y a qu'une seule voix, le bouton est grisé, pas
// accessible. Le switch [de fichier] n'apparaît que quand il y a un autre
// fichier avec une piste validée. Si le fichier a déjà français ET japonais,
// le switch de fichier n'a pas besoin d'apparaître. »
//
// Le lecteur avait DEUX commandes pour la même idée : le menu des pistes audio
// (les voix DU fichier) et le drapeau de langue (un AUTRE fichier). Ici une
// seule règle construit un seul menu : d'abord les pistes du fichier, puis —
// seulement pour les langues que le fichier n'a pas — les autres fichiers que
// le serveur a vus (`/api/voix`, qui lit le mapping et la sonde, jamais une
// résolution en direct).
//
// ⚠️ MODULE PUR : PAS DE DOM, PAS DE RÉSEAU, PAS D'IMPORT DU LECTEUR. C'est ce
// qui permet de l'éprouver en une milliseconde (`essais/voix-menu.mjs`) et
// d'être sûr que la règle ne dépend d'aucun état caché.
// ---------------------------------------------------------------------------

/** Les codes de langue tels que ffprobe les rend, et le mot qu'on affiche. */
export const LANGUES = Object.freeze({
  fr: 'Français', fre: 'Français', fra: 'Français',
  en: 'Anglais', eng: 'Anglais',
  ja: 'Japonais', jpn: 'Japonais', jp: 'Japonais',
  es: 'Espagnol', spa: 'Espagnol',
  de: 'Allemand', ger: 'Allemand', deu: 'Allemand',
  it: 'Italien', ita: 'Italien',
  pt: 'Portugais', por: 'Portugais',
  ko: 'Coréen', kor: 'Coréen',
  zh: 'Chinois', chi: 'Chinois', zho: 'Chinois',
  ru: 'Russe', rus: 'Russe',
  ar: 'Arabe', ara: 'Arabe',
  nl: 'Néerlandais', dut: 'Néerlandais', nld: 'Néerlandais',
});

/** Le mot d'une piste : « Français », « Japonais »… ou son code en capitales. */
export function nomDeLangue(code) {
  const c = String(code || '').toLowerCase();
  return LANGUES[c] ?? (c && c !== 'und' ? c.toUpperCase() : 'Piste audio');
}

const FRANCAIS = /^(fr|fre|fra)$/i;

/**
 * Ce qu'une piste VAUT pour la bascule : `vf` si elle est française, `vo` si
 * elle porte une langue connue qui n'est pas le français, `null` si elle ne
 * dit rien (`und`, vide). Une piste indéterminée ne compte pour aucune des
 * deux : on ne retire pas une proposition sur une supposition.
 */
function versionDePiste(code) {
  const c = String(code || '').toLowerCase();
  if (!c || c === 'und') return null;
  return FRANCAIS.test(c) ? 'vf' : 'vo';
}

/** Le mot d'une langue de bascule, selon l'œuvre : la VO d'une série n'est pas japonaise. */
function motDeVersion(langue, typeOeuvre) {
  if (langue === 'vf') return 'Français';
  return typeOeuvre === 'anime' ? 'Japonais' : 'Anglais';
}

/**
 * Les entrées du menu « Voix », dans l'ordre où elles s'affichent.
 *
 * @param {object} o
 * @param {Array<{langue?: string}>} [o.pistes] pistes audio du fichier courant (`/api/audio`).
 * @param {number} [o.audioActif] rang de la piste servie.
 * @param {Array<{langue: 'vf'|'vo', verifiee: boolean}>} [o.autres] langues portées
 *   par d'AUTRES fichiers vivants de l'épisode (`/api/voix`).
 * @param {'anime'|string|null} [o.typeOeuvre]
 * @param {(code: string) => string} [o.nom] nommage d'une piste (par défaut `nomDeLangue`).
 * @returns {Array<{libelle: string, rang?: number, autre?: {langue: string, verifiee: boolean}, actif: boolean}>}
 */
export function entreesDuMenuVoix({ pistes = [], audioActif = 0, autres = [], typeOeuvre = null, nom = nomDeLangue } = {}) {
  const entrees = [];

  // Une entrée par langue, dans l'ordre où les pistes se présentent — la
  // PREMIÈRE piste d'une langue la représente, comme `menuAudio` l'a toujours
  // fait (deux pistes japonaises 2.0 et 5.1 restent une seule voix ici).
  const parLangue = new Map();
  const versionsDuFichier = new Set();
  (Array.isArray(pistes) ? pistes : []).forEach((p, rang) => {
    const libelle = nom(p?.langue);
    if (!parLangue.has(libelle)) parLangue.set(libelle, rang);
    const v = versionDePiste(p?.langue);
    if (v) versionsDuFichier.add(v);
  });
  for (const [libelle, rang] of parLangue) {
    entrees.push({ libelle, rang, actif: rang === audioActif });
  }

  // Puis les autres fichiers — seulement pour une langue que le fichier N'A
  // PAS : « si le fichier a déjà français et japonais, le switch de fichier
  // n'a pas besoin d'apparaître ». Une langue, une entrée : le serveur trie
  // déjà ses candidats, le premier venu est le meilleur.
  const dejaProposees = new Set();
  for (const a of (Array.isArray(autres) ? autres : [])) {
    const langue = a?.langue === 'vf' ? 'vf' : (a?.langue === 'vo' ? 'vo' : null);
    if (!langue || versionsDuFichier.has(langue) || dejaProposees.has(langue)) continue;
    dejaProposees.add(langue);
    const verifiee = a.verifiee !== false;
    entrees.push({
      // « (non vérifié) » quand seul le nom du fichier annonce la langue : on
      // propose, mais on ne promet pas — c'est la règle du drapeau d'avant.
      libelle: `${motDeVersion(langue, typeOeuvre)} · autre fichier${verifiee ? '' : ' (non vérifié)'}`,
      autre: { langue, verifiee },
      actif: false,
    });
  }

  return entrees;
}

/** Un choix qui n'en est pas un ne mérite pas un bouton actif. */
export function boutonVoixActif(entrees) {
  return Array.isArray(entrees) && entrees.length >= 2;
}

// ---------------------------------------------------------------------------
// Le panneau « Changement de source » (propriétaire seulement).
// ---------------------------------------------------------------------------

/** « 1,2 Gio », « 700 Mio » — ou `null` quand le poids n'est pas connu. */
export function poidsLisible(octets) {
  const n = Number(octets);
  if (!Number.isFinite(n) || n <= 0) return null;
  const gio = n / 1024 ** 3;
  if (gio >= 1) {
    // Un chiffre après la virgule sous 10 Gio, aucun au-delà : « 20 Gio » se
    // lit mieux que « 20,0 Gio ».
    const v = gio >= 10 ? Math.round(gio) : Math.round(gio * 10) / 10;
    return `${String(v).replace('.', ',')} Gio`;
  }
  return `${Math.round(n / 1024 ** 2)} Mio`;
}

/** La qualité lue dans le nom du fichier — le serveur note la même chose dans le score. */
function qualiteDuNom(fichier) {
  const f = String(fichier || '');
  if (/2160p|4k\b|uhd/i.test(f)) return '4K';
  if (/1080p/i.test(f)) return '1080p';
  if (/720p/i.test(f)) return '720p';
  if (/480p|576p|\bsd\b|dvdrip/i.test(f)) return 'SD';
  return null;
}

/** VF / VO / MULTI, d'après les pistes RÉELLES quand la sonde les a vues, sinon d'après le nom. */
function badgeDeLangue(s) {
  const reelles = Array.isArray(s.verdict?.langues) ? s.verdict.langues : [];
  const versions = new Set();
  if (reelles.length) {
    for (const code of reelles) { const v = versionDePiste(code); if (v) versions.add(v); }
  } else {
    for (const l of (Array.isArray(s.languesAnnoncees) ? s.languesAnnoncees : [])) {
      if (l === 'vf' || l === 'vo') versions.add(l);
    }
  }
  if (versions.size >= 2) return 'MULTI';
  if (versions.has('vf')) return 'VF';
  if (versions.has('vo')) return 'VO';
  return null;
}

/**
 * Les lignes du panneau, DANS L'ORDRE REÇU : le tri est celui du serveur
 * (`ordonnerParScore`), et le panneau ne le refait pas.
 *
 * @param {Array<object>} sources la réponse de `/api/sources-episode`.
 * @param {{nomSource?: (source: string|null) => string}} [o]
 * @returns {Array<{url: string, titre: string, badges: string[], score: number, courant: boolean, exclu: boolean}>}
 */
export function lignesDuPanneauSources(sources, { nomSource = (s) => s } = {}) {
  return (Array.isArray(sources) ? sources : []).map((s) => {
    const badges = [];
    if (s.courant) badges.push('courant');
    if (s.epingle) badges.push('épingle');
    if (s.auto) badges.push('auto');
    if (s.cache) badges.push('⚡ cache');
    // Un lien mort n'est jamais « vérifié », même si une vieille sonde l'a vu.
    if (s.mort) badges.push('✗ mort');
    else if (s.verdict?.ok || s.verdict?.confirme) badges.push('✓ vérifié');
    if (s.direct === true) badges.push('direct');
    else if (s.direct === false) badges.push('remux');
    const langue = badgeDeLangue(s);
    if (langue) badges.push(langue);
    if (Array.isArray(s.verdict?.sousTitres) && s.verdict.sousTitres.length) badges.push('ST');
    const qualite = qualiteDuNom(s.fichier);
    if (qualite) badges.push(qualite);
    const poids = poidsLisible(s.poids);
    if (poids) badges.push(poids);

    const nom = nomSource(s.source ?? null) ?? s.source ?? '?';
    return {
      url: s.url,
      titre: `${nom} · ${s.fichier ?? s.url ?? ''}`,
      badges,
      score: Math.round(Number(s.score) || 0),
      courant: !!s.courant,
      exclu: !!s.exclu,
    };
  });
}
